import FontAwesomeV6Icon from '@code-dot-org/component-library/fontAwesomeV6Icon';
import {Button as MuiButton} from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import ReactTooltip from 'react-tooltip';

import {ViewType} from '@cdo/apps/code-studio/viewAsRedux';
import fontConstants from '@cdo/apps/fontConstants';
import FontAwesome from '@cdo/apps/legacySharedComponents/FontAwesome';
import {EVENTS} from '@cdo/apps/metrics/AnalyticsConstants';
import analyticsReporter from '@cdo/apps/metrics/AnalyticsReporter';
import color from '@cdo/apps/util/color';
import experiments from '@cdo/apps/util/experiments';
import i18n from '@cdo/locale';

import FocusAreaIndicator from './FocusAreaIndicator';
import ProgressBubbleSet from './ProgressBubbleSet';
import {
  lessonIsLockedForAllStudents,
  lessonIsLockedForUser,
  lessonIsVisible,
} from './progressHelpers';
import {levelWithProgressType, lessonType} from './progressTypes';

function SummaryProgressRow({
  dark,
  lesson,
  levels,
  lessonIsHiddenForStudents,
  lessonIsLockedForUser,
  lessonIsLockedForAllStudents,
  unitHasUnnumberedLessons,
  viewAs,
  isOnLevelView,
  unitId,
  unitName,
  userId,
  userType,
}) {
  // The parent component filters out hidden SummaryProgressRows from the student view,
  // this check is just to ensure it won't be rendered if it should be hidden for students
  if (lessonIsHiddenForStudents && viewAs === ViewType.Participant) {
    return null;
  }

  const isLockedForUser = lessonIsLockedForUser(lesson, levels, viewAs);
  const isLockedForSection = lessonIsLockedForAllStudents(lesson.id);
  const showAsLocked = isLockedForUser || isLockedForSection;

  let lessonTitle = lesson.name;
  if (lesson.lessonNumber && !unitHasUnnumberedLessons) {
    lessonTitle = lesson.lessonNumber + '. ' + lessonTitle;
  }

  // We want to exclude the Lesson Tutor button for assessment and survey lessons.
  // These lessons don't have lesson plans, so we can use that as a proxy for
  // whether or not to show the Lesson Tutor button.
  const showLessonTutorButton =
    lesson.lessonTutorPath && lesson.hasLessonPlan && userId;

  const handleLessonTutorClick = () => {
    analyticsReporter.sendEvent(EVENTS.LESSON_TUTOR_UNIT_OVERVIEW_CLICK, {
      lessonId: lesson.id,
      lessonName: lesson.name,
      unitId,
      unitName,
      userId,
      userType,
      view: 'summary-row',
    });
  };

  const displayDashedBorder = lessonIsHiddenForStudents || showAsLocked;

  const titleTooltipId = _.uniqueId();
  const lockedTooltipId = _.uniqueId();

  return (
    <tr
      id={`summary-progress-row-${lesson.lessonNumber}`}
      className="uitest-summary-progress-row"
      style={{
        ...(dark ? styles.darkRow : styles.lightRow),
        ...(displayDashedBorder && styles.dashedBorder),
      }}
    >
      <td
        style={{
          ...styles.col1,
          ...(isLockedForUser && styles.fadedCol),
        }}
      >
        <div style={styles.colText}>
          {lessonIsHiddenForStudents && (
            <FontAwesome icon="eye-slash" style={styles.icon} />
          )}
          {lesson.lockable && (
            <span data-tip data-for={lockedTooltipId}>
              <FontAwesome
                icon={showAsLocked ? 'lock' : 'unlock'}
                style={{
                  ...styles.icon,
                  ...(!showAsLocked && styles.unlockedIcon),
                }}
              />
              {!showAsLocked && viewAs === ViewType.Instructor && (
                <ReactTooltip
                  id={lockedTooltipId}
                  role="tooltip"
                  wrapper="span"
                  effect="solid"
                >
                  {i18n.lockAssessmentLong()}
                </ReactTooltip>
              )}
            </span>
          )}
          <span
            data-tip
            data-for={titleTooltipId}
            aria-describedby={titleTooltipId}
          >
            {lessonTitle}
            <ReactTooltip
              id={titleTooltipId}
              role="tooltip"
              wrapper="span"
              effect="solid"
            >
              {lesson.name}
            </ReactTooltip>
          </span>
        </div>
      </td>
      <td
        style={{
          ...styles.col2,
          ...(isLockedForUser && styles.fadedCol),
        }}
      >
        <div style={styles.col2Content}>
          <div style={styles.col2Left}>
            {levels.length === 0 ? (
              i18n.lessonContainsNoLevels()
            ) : (
              <ProgressBubbleSet
                levels={levels}
                disabled={isLockedForUser}
                style={lesson.isFocusArea ? styles.focusAreaMargin : undefined}
                lessonName={lesson.name}
              />
            )}
            {lesson.isFocusArea && <FocusAreaIndicator />}
          </div>
          {!isOnLevelView && (
            <div style={styles.buttonColumn}>
              {viewAs === ViewType.Participant &&
                lesson.student_lesson_plan_html_url && (
                  <MuiButton
                    className="ui-test-lesson-resources"
                    href={lesson.student_lesson_plan_html_url}
                    variant="contained"
                    color="white"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<FontAwesomeV6Icon iconName="file-lines" />}
                  >
                    {i18n.lessonResources()}
                  </MuiButton>
                )}
              {showLessonTutorButton &&
                experiments.isEnabledAllowingQueryString(
                  experiments.LESSON_TUTOR
                ) && (
                  <MuiButton
                    href={lesson.lessonTutorPath}
                    variant="contained"
                    color="white"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLessonTutorClick}
                    startIcon={
                      <FontAwesomeV6Icon
                        iconName="ai-bot-solid"
                        iconFamily="kit"
                      />
                    }
                  >
                    {'Lesson Tutor'}
                  </MuiButton>
                )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

SummaryProgressRow.propTypes = {
  dark: PropTypes.bool.isRequired,
  lesson: lessonType.isRequired,
  levels: PropTypes.arrayOf(levelWithProgressType).isRequired,
  isOnLevelView: PropTypes.bool,

  // from redux
  viewAs: PropTypes.oneOf(Object.keys(ViewType)),
  lessonIsHiddenForStudents: PropTypes.bool.isRequired,
  lessonIsLockedForUser: PropTypes.func.isRequired,
  lessonIsLockedForAllStudents: PropTypes.func.isRequired,
  unitHasUnnumberedLessons: PropTypes.bool.isRequired,
  unitId: PropTypes.number,
  unitName: PropTypes.string,
  userId: PropTypes.number,
  userType: PropTypes.string,
};

export const styles = {
  lightRow: {
    backgroundColor: color.table_light_row,
  },
  darkRow: {
    backgroundColor: color.table_dark_row,
  },
  dashedBorder: {
    borderStyle: 'dashed',
    borderWidth: 2,
  },
  col1: {
    width: 200,
    minWidth: 200,
    maxWidth: 200,
    lineHeight: '52px',
    color: color.charcoal,
    letterSpacing: -0.11,
    whiteSpace: 'nowrap',
    paddingLeft: 20,
    paddingRight: 20,
    borderRightWidth: 1,
    borderRightColor: color.border_light_gray,
    borderRightStyle: 'solid',
  },
  col2: {
    position: 'relative',
    width: '100%',
    paddingLeft: 20,
    paddingRight: 20,
  },
  col2Content: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  col2Left: {
    flex: 1,
  },
  buttonColumn: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    marginLeft: 10,
  },
  // When we set our opacity on the row element instead of on individual tds,
  // there are weird interactions with our tooltips in Chrome, and borders end
  // up disappearing.
  fadedCol: {
    opacity: 0.6,
  },
  colText: {
    color: color.charcoal,
    ...fontConstants['main-font-semi-bold'],
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  icon: {
    marginRight: 5,
    fontSize: 12,
    color: color.cyan,
  },
  unlockedIcon: {
    color: color.orange,
  },
  focusAreaMargin: {
    // Our focus area indicator is absolutely positioned. Add a margin when it's
    // there so that it wont overlap dots.
    marginRight: 130,
  },
  opaque: {
    opacity: 1,
  },
};

export const UnconnectedSummaryProgressRow = SummaryProgressRow;
export default connect((state, ownProps) => ({
  viewAs: state.viewAs,
  lessonIsHiddenForStudents: !lessonIsVisible(
    ownProps.lesson,
    state,
    ViewType.Participant
  ),
  unitHasUnnumberedLessons: state.progress.unitHasUnnumberedLessons,
  lessonIsLockedForUser: (lesson, levels, viewAs) =>
    lessonIsLockedForUser(lesson, levels, state, viewAs),
  lessonIsLockedForAllStudents: lessonId =>
    lessonIsLockedForAllStudents(lessonId, state),
  unitId: state.progress.scriptId,
  unitName: state.progress.unitTitle,
  userId: state.currentUser.userId,
  userType: state.currentUser.userType,
}))(SummaryProgressRow);
